import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useRouteError,
} from "react-router-dom";
import { MessageCircle, AlertCircle } from "lucide-react";
import { ToastProvider } from "./contexts/ToastContext";
import Toast from "./components/Toast/Toast";

// Error Boundary Component
function ErrorBoundary() {
  const error = useRouteError();
  console.error("Application Error:", error);
  
  // Handle chunk loading errors (common after new deployments)
  const isChunkError = error?.message?.includes("Loading chunk") || 
                       error?.message?.includes("module script");

  if (isChunkError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Update Available</h2>
        <p>A new version of the website is available. Please refresh to continue.</p>
        <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', borderRadius: '5px', cursor: 'pointer' }}>
          Refresh Now
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '20px',
      textAlign: 'center',
      fontFamily: 'Poppins, sans-serif'
    }}>
      <AlertCircle size={64} color="#7c5dfa" style={{ marginBottom: '20px' }} />
      <h1 style={{ fontSize: '2rem', marginBottom: '10px' }}>Oops! Something went wrong.</h1>
      <p style={{ color: '#666', marginBottom: '10px' }}>We apologize for the inconvenience. Please try refreshing the page or return home.</p>
      {process.env.NODE_ENV === 'development' && (
        <pre style={{ background: '#f4f4f4', padding: '10px', borderRadius: '5px', maxWidth: '80%', overflow: 'auto' }}>
          {error?.message || "Unknown error"}
        </pre>
      )}
      <button 
        onClick={() => window.location.href = '/'}
        style={{
          padding: '12px 30px',
          background: '#7c5dfa',
          color: 'white',
          border: 'none',
          borderRadius: '50px',
          fontWeight: '700',
          cursor: 'pointer',
          marginTop: '20px'
        }}
      >
        Go Back Home
      </button>
    </div>
  );
}

// Critical components
import Header from "./components/Navbar/Navbar.jsx";
import Footer from "./components/Footer/Footer.jsx";
import Slider from "./components/swiper/swiper.jsx";
import ScrollToTop from "./components/ui/ScrollToTop.jsx";
import LazySection from "./components/ui/LazySection.jsx";

// Lazy-loaded components
const Home = lazy(() => import("./components/Home/Home.jsx"));
const About = lazy(() => import("./components/About/About.jsx"));
const Serve = lazy(() => import("./components/Serve/Serve.jsx"));
const Accordion = lazy(() => import("./components/Accordion/Accordion.jsx"));
const Services = lazy(() => import("./components/Services/Services.jsx"));
const Contact = lazy(() => import("./components/contact/contactnew/contact/Contact.jsx"));
const SocialHub = lazy(() => import("./components/SocialHub/SocialHub.jsx"));
const ServiceDetail = lazy(() => import("./components/ServiceDetail/ServiceDetail.jsx"));
const Jobs = lazy(() => import("./components/Jobs/Jobs.jsx"));
const Blogs = lazy(() => import("./components/Blogs/Blogs.jsx"));
const BlogDetail = lazy(() => import("./components/BlogDetail/BlogDetail.jsx"));
const Photos = lazy(() => import("./components/SocialHub/SeeMore/photos"));
const BusinessConsulting = lazy(() => import("./components/Services/BusinessConsulting/BusinessConsulting.jsx"));
const Recruitment = lazy(() => import("./components/Services/Recruitment/Recruitment.jsx"));
const CorporateTraining = lazy(() => import("./components/Services/CorporateTraining/CorporateTraining.jsx"));
const SoftwareDevelopment = lazy(() => import("./components/Services/SoftwareDevelopment/SoftwareDevelopment.jsx"));
const Collaboration = lazy(() => import("./components/collaboration/collaboration.jsx"));
const Founder = lazy(() => import("./components/About/founder/founder.jsx"));
const CollaborationNew = lazy(() => import("./components/collaboration/collaborationnew/collaborationnew.jsx"));
const Serviceinpage = lazy(() => import("./components/ServiceDetail/Serviceinpage/Serviceinpage.jsx"));
const Blogsnew = lazy(() => import("./components/Blogs/Blogsnew/Blogsnew.jsx"));
const ContactNew = lazy(() => import("./components/contact/contactnew/contactnew.jsx"));
const Newsletter = lazy(() => import("./components/Newsletter/Newsletter.jsx"));
const Login = lazy(() => import("./components/Newsletter/login/login.jsx"));
const Open = lazy(() => import("./components/Newsletter/login/open/open.jsx"));
const AdminDashboard = lazy(() => import("./components/Newsletter/Admin/AdminDashboard.jsx"));
const PageDetails = lazy(() => import("./components/Newsletter/NewsletterDetails/PageDetails.jsx"));
const InsightUpload = lazy(() => import("./components/InsightUpload/InsightUpload.jsx"));
const InsightGallery = lazy(() => import("./components/InsightGallery/InsightGallery.jsx"));
const InsightAdminDashboard = lazy(() => import("./components/AdminDashboard/AdminDashboard.jsx"));

import axios from "axios";
import { API_BASE } from "./config/api";
import "./App.css";
import { Wifi, WifiOff, Loader2 } from "lucide-react";

axios.defaults.baseURL = API_BASE;
axios.defaults.withCredentials = true; 

// Connection Status Component
const ConnectionStatus = () => {
  const [status, setStatus] = useState("checking"); // 'online', 'offline', 'checking'

  useEffect(() => {
    const checkConnection = async () => {
      const endpoints = ["/api/health", "/api/photos"];
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint, { timeout: 8000 });
          if (response.status === 200) {
            setStatus("online");
            return;
          }
        } catch (err) {
          console.warn(`Backend check failed for ${endpoint}:`, err.message);
        }
      }
      setStatus("offline");
    };

    checkConnection();
    const interval = setInterval(checkConnection, 10000); // Check every 10s
    return () => clearInterval(interval);
  }, []);

  if (status === "checking" || status === "online") return null;

  return (
    <div className={`connection-status-pill ${status}`}>
      {status === "online" ? (
        <><Wifi size={14} /> <span>Backend Connected</span></>
      ) : (
        <><WifiOff size={14} /> <span>Backend API Unreachable</span></>
      )}
    </div>
  );
};

function RootLayout() {
  return (
    <div className="site-root">
      <ScrollToTop />
      <Header />
      <main>
        <Suspense fallback={<div className="loading-fallback" style={{height: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Loading...</div>}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <a
        className="floating-connect"
        href="https://wa.me/919488161565"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Connect on WhatsApp - +91 94881 61565"
      >
        <span className="floating-connect-label">Connect</span>
        <span className="floating-connect-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="32"
            height="32"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .018 5.396.015 12.03c0 2.12.553 4.189 1.606 6.006L0 24l6.135-1.61a11.81 11.81 0 005.91 1.586h.005c6.637 0 12.032-5.396 12.035-12.032a11.762 11.762 0 00-3.48-8.504z" />
          </svg>
        </span>
      </a>
    </div>
  );
}

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <RootLayout />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: (
            <>
              <Slider />
              <Home />
              <LazySection height="500px"><Jobs /></LazySection>
              <LazySection height="500px"><About /></LazySection>
              <LazySection height="600px"><Services /></LazySection>
              <LazySection height="400px"><Serve /></LazySection>
              <LazySection height="400px"><Accordion /></LazySection>
              <LazySection height="500px"><Blogs /></LazySection>
              <LazySection height="300px"><Collaboration /></LazySection>
              <SocialHub />
              <LazySection height="500px"><Contact /></LazySection>
            </>
          ),
        },
        { path: "services/:id", element: <ServiceDetail /> },
        { path: "business-consulting", element: <BusinessConsulting /> },
        { path: "recruitment", element: <Recruitment /> },
        { path: "corporate-training", element: <CorporateTraining /> },
        { path: "software-development", element: <SoftwareDevelopment /> },
        { path: "jobs", element: <Jobs /> },
        { path: "photos", element: <Photos /> },
        { path: "blog/:id", element: <BlogDetail /> },
        { path: "about", element: <Founder /> },
        { path: "collaboration", element: <CollaborationNew /> },
        { path: "service-inpage", element: <Serviceinpage /> },
        { path: "blogs-new", element: <Blogsnew /> },
        { path: "contact-new", element: <ContactNew /> },
        { path: "newsletter", element: <Newsletter /> },
        { path: "newsletter/:id", element: <PageDetails /> },
        { path: "login", element: <Login /> },
        { path: "open", element: <Open /> },
        { path: "admin-dashboard", element: <AdminDashboard /> },
        { path: "insights/upload", element: <InsightUpload /> },
        { path: "insights", element: <InsightGallery /> },
        { path: "insights/admin", element: <InsightAdminDashboard /> },
        { path: "*", element: <Navigate to="/" replace /> },
      ],
    },
  ],
  {
    // opt into v7 behaviors to silence deprecation warnings
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

export default function App() {
  return (
    <ToastProvider>
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
        }}
      />
      <Toast />
    </ToastProvider>
  );
}
